/**
 * Cache Management Utility
 * Handles cache busting and ensures users get the latest content
 */

class CacheManager {
  private static instance: CacheManager;
  private version: string;
  private lastUpdate: number;
  private updateCheckEnabled: boolean = true;

  private constructor() {
    // Use build timestamp or version from environment
    this.version = import.meta.env.VITE_VERSION ||
                   import.meta.env.VITE_BUILD_TIME ||
                   Date.now().toString();
    this.lastUpdate = Date.now();
  }

  public static getInstance(): CacheManager {
    if (!CacheManager.instance) {
      CacheManager.instance = new CacheManager();
    }
    return CacheManager.instance;
  }

  /**
   * Get current app version for cache busting
   */
  public getVersion(): string {
    return this.version;
  }

  /**
   * Add cache busting parameters to URLs
   */
  public bustUrl(url: string): string {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}v=${this.version}&t=${Date.now()}`;
  }

  /**
   * Set cache headers for fetch requests
   */
  public getCacheHeaders(): HeadersInit {
    return {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0',
      'If-Modified-Since': new Date(0).toUTCString(),
      'X-App-Version': this.version
    };
  }

  /**
   * Clear all browser caches
   */
  public async clearAllCaches(): Promise<void> {
    try {
      // Clear HTTP cache
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
      }

      // Clear localStorage cache entries (keep auth data)
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key.startsWith('cache_') ||
          key.startsWith('api_cache_') ||
          key.startsWith('resource_cache_')
        )) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      // Clear sessionStorage cache entries
      const sessionKeysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && (
          key.startsWith('cache_') ||
          key.startsWith('api_cache_') ||
          key.startsWith('resource_cache_')
        )) {
          sessionKeysToRemove.push(key);
        }
      }
      sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));

      console.log('All caches cleared successfully');
    } catch (error) {
      console.error('Error clearing caches:', error);
    }
  }

  /**
   * Force reload the page with cache bypass
   */
  public forceReload(): void {
    // Add timestamp to current URL to bypass cache
    const url = new URL(window.location.href);
    url.searchParams.set('_t', Date.now().toString());
    window.location.href = url.toString();
  }

  /**
   * Check if cache should be invalidated
   */
  public shouldInvalidateCache(): boolean {
    const lastVersion = localStorage.getItem('app_version');
    const currentVersion = this.getVersion();

    if (lastVersion !== currentVersion) {
      localStorage.setItem('app_version', currentVersion);
      return true;
    }

    return false;
  }

  /**
   * Add cache busting to dynamic imports
   */
  public async dynamicImport<T>(modulePath: string): Promise<T> {
    const bustedPath = this.bustUrl(modulePath);
    return import(/* @vite-ignore */ bustedPath);
  }

  /**
   * Set up automatic cache invalidation
   */
  public setupAutoInvalidation(): void {
    // Check for cache invalidation on page load
    if (this.shouldInvalidateCache()) {
      this.clearAllCaches();
    }

    // Skip update checking if disabled via environment variable
    if (import.meta.env.VITE_DISABLE_UPDATE_CHECK === 'true') {
      console.debug('Update checking disabled via environment variable');
      this.updateCheckEnabled = false;
      return;
    }

    // Set up periodic cache checks (configurable interval - default 2 hours)
    const interval = parseInt(import.meta.env.VITE_UPDATE_CHECK_INTERVAL || '7200000');
    setInterval(() => {
      if (this.updateCheckEnabled) {
        this.checkForUpdates();
      }
    }, interval);

    // Check for updates when page becomes visible
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.updateCheckEnabled) {
        this.checkForUpdates();
      }
    });
  }

  /**
   * Check for app updates
   */
  private async checkForUpdates(): Promise<void> {
    // Temporarily disabled - version endpoint not configured in Kong
    // This prevents CORS errors in the console
    return;
  }

  /**
   * Notify user of available update
   */
  private notifyUserOfUpdate(): void {
    const updateEvent = new CustomEvent('app-update-available', {
      detail: { newVersion: true }
    });
    window.dispatchEvent(updateEvent);
  }
}

export default CacheManager;